import {
  SKIP_HUB_ID,
  type IDropdownItemStations,
  type ISelectListNumberItem,
} from "@ryde/shared";

const isPositiveHubStationId = (id: number | undefined): id is number =>
  typeof id === "number" && id > 0;

export const getHubStationItemId = (
  station: IDropdownItemStations,
): number | undefined => {
  const id = station.id ?? (station as { value?: number }).value;

  return isPositiveHubStationId(id) ? id : undefined;
};

/** True when hub is not stored and not explicitly skipped (-1 means "no hub" by user choice). */
export const isUnsetHubId = (hubId: number | null | undefined): boolean => {
  if (hubId === SKIP_HUB_ID) {
    return false;
  }

  return hubId === null || hubId === undefined || hubId === 0;
};

/** True when a stored hub is not offered for the current station pair; an explicit skip is never stale. */
export const isStaleHubStationId = ({
  hubStationId,
  hubStations,
}: {
  hubStationId: number | null | undefined;
  hubStations: IDropdownItemStations[];
}): boolean =>
  isPositiveHubStationId(hubStationId ?? undefined) &&
  !hubStations.some(station => getHubStationItemId(station) === hubStationId);

export const resolveDefaultHubStationId = (
  stations: IDropdownItemStations[] | null | undefined,
): number | undefined => {
  if (!stations?.length) {
    return undefined;
  }

  // Prefer the station flagged as default; if exactly one option exists, treat it as the default.
  const flaggedDefault = stations.find(station => station.isDefault);

  if (flaggedDefault) {
    return getHubStationItemId(flaggedDefault);
  }

  if (stations.length === 1) {
    return getHubStationItemId(stations[0]);
  }

  return undefined;
};

export const mapHubStationsToSelectItems = (
  stations: IDropdownItemStations[] | null | undefined,
): ISelectListNumberItem[] =>
  (stations ?? []).map(s => ({
    value: getHubStationItemId(s) ?? 0,
    label: s.label ?? (s as { name?: string }).name ?? "",
  }));
